import { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FEATURE_FLAG_KEYS, FEATURE_FLAG_LABELS, type FeatureFlagKey } from '@marketing-hub/shared';
import { apiFetch } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { Field, TextInput } from '@/components/ui/Field';
import { ErrorState, LoadingState } from '@/components/StateMessage/StateMessage';
import { useToast } from '@/components/toast/ToastProvider';
import { useProjectContext } from '@/project/ProjectContext';
import { formatDateTime } from '@/lib/format';
import { downloadApiFile } from '@/lib/downloadApi';
import { ComplianceSettingsPanel } from '@/features/settings/ComplianceSettingsPanel';
import styles from './SettingsPage.module.css';

const TABS = [
  { id: 'privacy', label: 'Privacy & AVG', to: '/app/instellingen/privacy' },
  { id: 'compliance', label: 'Compliance lane', to: '/app/instellingen/compliance' },
  { id: 'audit', label: 'Auditlog', to: '/app/instellingen/audit' },
  { id: 'platform', label: 'Platform', to: '/app/instellingen/platform' },
] as const;

type TabId = (typeof TABS)[number]['id'];

export function SettingsPage({ tab }: { tab: TabId }) {
  const { selectedProjectId } = useProjectContext();
  const toast = useToast();
  const qc = useQueryClient();
  const projectId = selectedProjectId;

  const ctxQ = useQuery({
    queryKey: ['platform-context', projectId],
    queryFn: () =>
      apiFetch<{
        my_org_role: string | null;
        privacy: { policy_url: string };
        maintenance: { enabled: boolean; message: string };
      }>(`/api/platform/context?project_id=${projectId ?? ''}`),
  });

  const consentQ = useQuery({
    queryKey: ['consent-log', projectId],
    enabled: Boolean(projectId) && tab === 'privacy',
    queryFn: () =>
      apiFetch<{ entries: Record<string, unknown>[] }>(
        `/api/platform/consent-log?project_id=${projectId}&limit=50`,
      ),
  });

  const auditQ = useQuery({
    queryKey: ['admin-audit'],
    enabled: tab === 'audit',
    queryFn: () =>
      apiFetch<{ entries: Record<string, unknown>[] }>('/api/platform/admin-audit?limit=50'),
  });

  const flagsQ = useQuery({
    queryKey: ['feature-flags'],
    enabled: tab === 'platform' && ctxQ.data?.my_org_role === 'owner',
    queryFn: () =>
      apiFetch<{ flags: { flag_key: string; project_id: string | null; enabled: boolean }[] }>(
        '/api/platform/feature-flags',
      ),
  });

  const llmStatusQ = useQuery({
    queryKey: ['llm-status'],
    enabled: tab === 'platform',
    queryFn: () =>
      apiFetch<{
        enabled: boolean;
        configured: boolean;
        provider: string;
        model: string;
        fallback_enabled: boolean;
        fallback_provider: string;
        provider_chain: string[];
        providers?: Record<
          string,
          { configured: boolean; api_key_env_primary: string }
        >;
        project_monthly_budget_usd: number;
        api_key_env_primary?: string;
        api_key_env_alternates?: string[];
        api_key_env_present?: Record<string, boolean>;
      }>('/api/llm/status'),
  });

  const llmSettingsMut = useMutation({
    mutationFn: (body: {
      provider?: 'openai' | 'openrouter';
      fallback_enabled?: boolean;
    }) => apiFetch('/api/llm/settings', { method: 'PATCH', body: JSON.stringify(body) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['llm-status'] });
      toast.push('LLM-provider opgeslagen', 'success');
    },
    onError: (e: Error) => toast.push(e.message, 'error'),
  });

  const llmProjectUsageQ = useQuery({
    queryKey: ['llm-usage', projectId],
    enabled: tab === 'platform' && Boolean(projectId),
    queryFn: () =>
      apiFetch<{
        summary: {
          requests: number;
          total_tokens: number;
          estimated_cost_usd: number;
          month_to_date_cost_usd: number;
          month_budget_usd: number;
        };
        by_feature: { feature: string; requests: number; estimated_cost_usd: number }[];
      }>(`/api/llm/usage?project_id=${projectId}&days=30`),
  });

  const llmOrgUsageQ = useQuery({
    queryKey: ['llm-org-usage'],
    enabled:
      tab === 'platform' &&
      (ctxQ.data?.my_org_role === 'owner' || ctxQ.data?.my_org_role === 'admin'),
    queryFn: () =>
      apiFetch<{
        summary: {
          requests: number;
          estimated_cost_usd: number;
          month_to_date_cost_usd: number;
          org_month_budget_usd: number | null;
        };
        by_project: {
          project_id: string;
          project_name: string;
          requests: number;
          estimated_cost_usd: number;
        }[];
      }>('/api/platform/llm/usage?days=30'),
  });

  const [maintMsg, setMaintMsg] = useState('');
  const maintenanceMut = useMutation({
    mutationFn: (body: { enabled: boolean; message?: string }) =>
      apiFetch('/api/platform/maintenance', { method: 'PATCH', body: JSON.stringify(body) }),
    onSuccess: () => {
      toast.push('Onderhoudsmodus bijgewerkt', 'success');
      void qc.invalidateQueries({ queryKey: ['platform-context'] });
      void qc.invalidateQueries({ queryKey: ['platform', 'public'] });
    },
    onError: (e: Error) => toast.push(e.message, 'error'),
  });

  const flagMut = useMutation({
    mutationFn: (body: { flag_key: string; enabled: boolean; project_id?: string | null }) =>
      apiFetch('/api/platform/feature-flags', { method: 'PATCH', body: JSON.stringify(body) }),
    onSuccess: () => {
      toast.push('Feature flag opgeslagen', 'success');
      void qc.invalidateQueries({ queryKey: ['feature-flags'] });
      void qc.invalidateQueries({ queryKey: ['platform-context'] });
    },
    onError: (e: Error) => toast.push(e.message, 'error'),
  });

  if (ctxQ.isLoading) return <LoadingState />;
  if (ctxQ.isError) return <ErrorState message={(ctxQ.error as Error).message} />;

  const role = ctxQ.data?.my_org_role;
  const isAdmin = role === 'owner' || role === 'admin';
  const isOwner = role === 'owner';
  const policyUrl = ctxQ.data?.privacy.policy_url ?? 'https://giovannipira.com/privacy';

  return (
    <div>
      <nav className={styles.breadcrumbs} aria-label="Broodkruimels">
        <Link to="/app/dashboard">Dashboard</Link>
        <span aria-hidden="true"> / </span>
        <span>Instellingen</span>
      </nav>
      <h1 className={styles.title}>Instellingen</h1>
      <div className={styles.tabs} role="tablist">
        {TABS.map((t) => (
          <NavLink
            key={t.id}
            to={t.to}
            className={({ isActive }) =>
              [styles.tab, isActive ? styles.tabActive : ''].filter(Boolean).join(' ')
            }
            role="tab"
          >
            {t.label}
          </NavLink>
        ))}
      </div>

      {tab === 'compliance' ? (
        <section className={styles.card}>
          <h2>Compliance lane</h2>
          {!projectId ? (
            <p>Selecteer een project om lane en automation-steps te configureren.</p>
          ) : (
            <ComplianceSettingsPanel projectId={projectId} />
          )}
        </section>
      ) : null}

      {tab === 'privacy' ? (
        <section className={styles.card}>
          <h2>Privacy, AVG & e-mailmarketing</h2>
          <p>
            <a href={policyUrl} target="_blank" rel="noreferrer">
              Privacybeleid (extern)
            </a>
            {' · '}
            <a href="/compliance/dpia-checklist.md" target="_blank" rel="noreferrer">
              DPIA-checklist (doc)
            </a>
          </p>
          <div className={styles.callout}>
            <strong>Telecomwet / e-mail</strong>
            <p>
              B2B cold outreach vereist een gerechtvaardigd belang of toestemming, plus een
              duidelijke afmelding en identificatie van de afzender. B2C vereist doorgaans
              voorafgaande, aantoonbare toestemming — log dit in het CRM (grondslag +
              consent-datum). Gebruik &quot;Niet contacteren&quot; direct bij opt-out.
            </p>
          </div>
          {!projectId ? (
            <p>Selecteer een project om consent-log en export te zien.</p>
          ) : !isAdmin ? (
            <p>Alleen beheerders zien consent-log en verwerkingsregister-export.</p>
          ) : (
            <>
              <p>
                <Button
                  variant="ghost"
                  onClick={() =>
                    void downloadApiFile(
                      `/api/platform/processing-register?project_id=${projectId}`,
                      'verwerkingsregister.csv',
                    ).catch((e: Error) => toast.push(e.message, 'error'))
                  }
                >
                  Verwerkingsregister exporteren (CSV)
                </Button>
              </p>
              {consentQ.isLoading ? <LoadingState /> : null}
              {consentQ.data?.entries.length ? (
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Tijd</th>
                      <th>Actie</th>
                      <th>Grondslag</th>
                      <th>Lead</th>
                    </tr>
                  </thead>
                  <tbody>
                    {consentQ.data.entries.map((e) => (
                      <tr key={String(e.id)}>
                        <td>{formatDateTime(String(e.created_at))}</td>
                        <td>{String(e.action)}</td>
                        <td>{String(e.legal_basis ?? '—')}</td>
                        <td>{String(e.lead_id ?? '').slice(0, 8)}…</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p>Nog geen consent-registraties voor dit project.</p>
              )}
            </>
          )}
        </section>
      ) : null}

      {tab === 'audit' ? (
        <section className={styles.card}>
          <h2>Admin auditlog</h2>
          {!isAdmin ? (
            <p>Alleen beheerders kunnen de auditlog inzien.</p>
          ) : (
            <>
              <p>
                <Button
                  variant="ghost"
                  onClick={() =>
                    void downloadApiFile(
                      '/api/platform/admin-audit/export?limit=200',
                      'admin-audit.csv',
                    ).catch((e: Error) => toast.push(e.message, 'error'))
                  }
                >
                  Export CSV
                </Button>
              </p>
              {auditQ.isLoading ? <LoadingState /> : null}
              {auditQ.data?.entries.length ? (
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Tijd</th>
                      <th>Actor</th>
                      <th>Actie</th>
                      <th>Resource</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditQ.data.entries.map((e) => (
                      <tr key={String(e.id)}>
                        <td>{formatDateTime(String(e.created_at))}</td>
                        <td>{String(e.actor_email ?? '—')}</td>
                        <td>{String(e.action)}</td>
                        <td>
                          {String(e.resource_type ?? '')}
                          {e.resource_id ? ` ${String(e.resource_id).slice(0, 8)}…` : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p>Nog geen admin-acties gelogd.</p>
              )}
            </>
          )}
        </section>
      ) : null}

      {tab === 'platform' ? (
        <section className={styles.card}>
          <h2>Platform & betrouwbaarheid</h2>
          {!isOwner ? (
            <p>Alleen de organisatie-eigenaar wijzigt onderhoudsmodus en feature flags.</p>
          ) : (
            <>
              <h3>Onderhoudsmodus</h3>
              <p>Huidige status: {ctxQ.data?.maintenance.enabled ? 'actief' : 'uit'}</p>
              <Field label="Bannerbericht">
                <TextInput
                  value={maintMsg}
                  onChange={(e) => setMaintMsg(e.target.value)}
                  placeholder={
                    ctxQ.data?.maintenance.message || 'Optioneel bericht voor gebruikers'
                  }
                />
              </Field>
              <div className={styles.row}>
                <Button
                  onClick={() =>
                    maintenanceMut.mutate({
                      enabled: true,
                      message: maintMsg || ctxQ.data?.maintenance.message,
                    })
                  }
                  disabled={maintenanceMut.isPending}
                >
                  Onderhoud aanzetten
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => maintenanceMut.mutate({ enabled: false })}
                  disabled={maintenanceMut.isPending}
                >
                  Onderhoud uit
                </Button>
              </div>
              <h3>Feature flags</h3>
              <ul className={styles.flagList}>
                {FEATURE_FLAG_KEYS.map((key: FeatureFlagKey) => {
                  const global = flagsQ.data?.flags.find(
                    (f) => f.flag_key === key && f.project_id === null,
                  );
                  const enabled = global?.enabled ?? false;
                  return (
                    <li key={key}>
                      <label>
                        <input
                          type="checkbox"
                          checked={enabled}
                          onChange={(e) =>
                            flagMut.mutate({
                              flag_key: key,
                              enabled: e.target.checked,
                              project_id: null,
                            })
                          }
                        />{' '}
                        {FEATURE_FLAG_LABELS[key]}
                      </label>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
          <h3>LLM gateway (site-breed)</h3>
          {llmStatusQ.isLoading ? <LoadingState /> : null}
          {llmStatusQ.data ? (
            <>
              <p>
                Status: {llmStatusQ.data.enabled ? 'aan' : 'uit'} · Actief:{' '}
                <strong>{llmStatusQ.data.provider}</strong>
                {llmStatusQ.data.provider_chain.length > 1
                  ? ` → fallback ${llmStatusQ.data.provider_chain.slice(1).join(' → ')}`
                  : ''}{' '}
                · Model: {llmStatusQ.data.model}
              </p>
              <ul className={styles.flagList}>
                {(['openrouter', 'openai'] as const).map((p) => {
                  const st = llmStatusQ.data.providers?.[p];
                  const envName = st?.api_key_env_primary ?? (p === 'openrouter' ? 'OPENROUTER_API_KEY' : 'OPENAI_API_KEY');
                  return (
                    <li key={p}>
                      {p === 'openrouter' ? 'OpenRouter' : 'OpenAI'}:{' '}
                      {st?.configured ? `${envName} gezet` : `${envName} ontbreekt in server-.env`}
                    </li>
                  );
                })}
              </ul>
              {isOwner ? (
                <div className={styles.llmSettings}>
                  <Field label="Primaire provider">
                    <select
                      className={styles.select}
                      value={
                        llmStatusQ.data.provider === 'openai' ? 'openai' : 'openrouter'
                      }
                      onChange={(e) =>
                        llmSettingsMut.mutate({
                          provider: e.target.value as 'openai' | 'openrouter',
                        })
                      }
                      disabled={llmSettingsMut.isPending}
                    >
                      <option value="openrouter">OpenRouter</option>
                      <option value="openai">OpenAI (direct)</option>
                    </select>
                  </Field>
                  {llmStatusQ.data.provider === 'openrouter' ? (
                    <label className={styles.checkRow}>
                      <input
                        type="checkbox"
                        checked={llmStatusQ.data.fallback_enabled}
                        onChange={(e) =>
                          llmSettingsMut.mutate({ fallback_enabled: e.target.checked })
                        }
                        disabled={llmSettingsMut.isPending}
                      />
                      Fallback naar OpenAI als OpenRouter faalt (402/5xx/timeout)
                    </label>
                  ) : null}
                  <p className={styles.hint}>
                    API-keys staan alleen in <code>/opt/marketing-hub/.env</code> op de server —
                    herstart <code>api</code> en <code>worker</code> na wijziging.
                  </p>
                </div>
              ) : null}
              {!llmStatusQ.data.configured ? (
                <p className={styles.warn}>
                  Geen werkende provider-keten — vul minstens de key van je primaire provider in
                  (.env).
                </p>
              ) : null}
            </>
          ) : null}
          {projectId && llmProjectUsageQ.data ? (
            <>
              <h4>Verbruik dit project (30 dagen)</h4>
              <p>
                {llmProjectUsageQ.data.summary.requests} requests ·{' '}
                {llmProjectUsageQ.data.summary.total_tokens.toLocaleString('nl-NL')} tokens · ca. $
                {llmProjectUsageQ.data.summary.estimated_cost_usd.toFixed(2)} · MTD $
                {llmProjectUsageQ.data.summary.month_to_date_cost_usd.toFixed(2)} / budget $
                {llmProjectUsageQ.data.summary.month_budget_usd}
              </p>
              {llmProjectUsageQ.data.by_feature.length ? (
                <ul className={styles.flagList}>
                  {llmProjectUsageQ.data.by_feature.map((f) => (
                    <li key={f.feature}>
                      {f.feature}: {f.requests}× · ${f.estimated_cost_usd.toFixed(4)}
                    </li>
                  ))}
                </ul>
              ) : null}
            </>
          ) : null}
          {isAdmin && llmOrgUsageQ.data ? (
            <>
              <h4>Verbruik organisatie (30 dagen)</h4>
              <p>
                {llmOrgUsageQ.data.summary.requests} requests · ca. $
                {llmOrgUsageQ.data.summary.estimated_cost_usd.toFixed(2)} · MTD $
                {llmOrgUsageQ.data.summary.month_to_date_cost_usd.toFixed(2)}
                {llmOrgUsageQ.data.summary.org_month_budget_usd != null
                  ? ` / budget $${llmOrgUsageQ.data.summary.org_month_budget_usd}`
                  : ''}
              </p>
              {llmOrgUsageQ.data.by_project.length ? (
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Project</th>
                      <th>Requests</th>
                      <th>Kosten (est.)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {llmOrgUsageQ.data.by_project.map((p) => (
                      <tr key={p.project_id}>
                        <td>{p.project_name}</td>
                        <td>{p.requests}</td>
                        <td>${p.estimated_cost_usd.toFixed(4)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : null}
            </>
          ) : null}
          <h3>Health</h3>
          <p>
            API: <code>/health</code> · Worker: poort 3099 (zie deploy-runbook).
          </p>
        </section>
      ) : null}
    </div>
  );
}
